import {
    Button,
    Flex,
    Input,
    Pagination,
    type RowProps,
    Space,
    Spin,
    Table,
    Tabs,
    Typography,
    type TableProps
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { SchoolService } from '../../../services/SchoolService'
import type { School } from '../../../types'
import { CreateSchoolModal } from '../../components/CreateSchoolModal'

import { ActiveUserCell } from './components/ActiveUserCell'
import { SeatCountCell } from './components/SeatCountCell'
import { UnassignedCountCell } from './components/UnassignedCountCell'

const Row: React.FC<Readonly<RowProps>> = props => {
    const style: React.CSSProperties = {
        ...props.style,
        cursor: 'pointer'
    }

    return <tr {...props} style={style} />
}

export const SchoolDashboardPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const searchFromQueryParam = searchParams.get('v')

    const [schools, setSchools] = useState<School[]>()
    const [totalCount, setTotalCount] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [offset, setOffset] = useState(0)

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const limit = 50 // Set a constant limit per page

    const search = async ({ searchOffset, searchValue }: { searchOffset: number; searchValue: string }) => {
        try {
            setIsLoading(true)
            const results = await SchoolService.search({
                offset: searchOffset,
                limit,
                searchValue
            })

            setSchools(results.schools)
            setTotalCount(results.totalCount)
        } catch (e) {
            console.error('Error retrieving schools', e)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        search({
            searchOffset: offset,
            searchValue: searchFromQueryParam ?? ''
        })
    }, [searchFromQueryParam])

    const navigate = useNavigate()

    const columns: TableProps<School>['columns'] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country'
        },
        {
            title: 'Postal Code',
            dataIndex: 'postalCode',
            key: 'postalCode'
        },
        {
            title: 'Domain',
            dataIndex: 'domain',
            key: 'domain'
        },
        {
            title: 'Active / Invited',
            key: 'active-invited',
            dataIndex: 'schoolSubscriptions',
            render: ss => {
                return <ActiveUserCell schoolSubscriptions={ss} />
            }
        },
        {
            title: 'Seat Count',
            key: 'seatCount',
            dataIndex: 'schoolSubscriptions',
            render: ss => {
                return <SeatCountCell schoolSubscriptions={ss} />
            }
        },
        {
            title: 'Seats Available',
            key: 'seatCount',
            dataIndex: 'schoolSubscriptions',
            render: ss => {
                return <UnassignedCountCell schoolSubscriptions={ss} />
            }
        },
        {
            title: 'Created',
            key: 'created',
            dataIndex: 'createdAt',
            render: (_, { createdAt }) => <>{dayjs(createdAt).format('YYYY/MM/DD HH:mm:ss')} </>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        onClick={() => {
                            navigate(`/schools/${record.id}`)
                        }}
                    >
                        View
                    </Button>
                </Space>
            )
        }
    ]

    if (!schools) return <Spin />

    return (
        <>
            <Flex justify="space-between" align="center">
                <Typography.Title>Schools</Typography.Title>
                <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                    + Create School
                </Button>
            </Flex>
            <Tabs style={{ padding: '20px' }}>
                <Tabs.TabPane tab="Schools List" key="list">
                    <Flex gap="middle" vertical>
                        <Flex justify="space-between" align="center">
                            <Input.Search
                                placeholder="Search schools..."
                                loading={isLoading}
                                enterButton
                                allowClear
                                style={{ width: '50%' }}
                                defaultValue={searchFromQueryParam ?? ''}
                                onSearch={val => {
                                    setSearchParams({ v: val })
                                }}
                            />
                            <Typography.Text style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                Total: {totalCount}
                            </Typography.Text>
                        </Flex>
                        <Table
                            columns={columns}
                            dataSource={schools}
                            rowKey="schoolId"
                            pagination={false}
                            components={{
                                body: { row: Row }
                            }}
                            onRow={record => {
                                return {
                                    onClick: () => {
                                        navigate(`/schools/${record.id}`)
                                    } // click row
                                }
                            }}
                        />
                        <Pagination
                            current={Math.floor(offset / limit) + 1}
                            onChange={async page => {
                                const newOffset = (page - 1) * limit
                                setOffset(newOffset)
                                await search({
                                    searchOffset: newOffset,
                                    searchValue: searchFromQueryParam ?? ''
                                })
                            }}
                            total={totalCount}
                            pageSize={limit}
                        />
                    </Flex>
                </Tabs.TabPane>
            </Tabs>
            <CreateSchoolModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
        </>
    )
}
